import React, { useMemo } from 'react';
import { useImageLoader } from '@/hooks/use-image-loader';
import { cn } from '@/lib/utils';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallbackSrc?: string;
  showSkeleton?: boolean;
  fadeInDuration?: number;
  onImageLoad?: () => void;
  onImageError?: (error: string) => void;
  rounded?: boolean; // for avatars/profile
  noRadius?: boolean; // disables internal border radius
  loading?: 'eager' | 'lazy'; // prioritize loading for first items
  retryCount?: number;
  retryDelay?: number;
}

export const Image: React.FC<ImageProps> = ({
  src,
  fallbackSrc,
  showSkeleton = true,
  fadeInDuration = 300,
  className,
  onImageLoad,
  onImageError,
  alt,
  rounded = false,
  noRadius = false,
  loading = 'lazy', // default to lazy loading
  retryCount = 2,
  retryDelay = 1000,
  ...props
}) => {
  // Memoize the src to prevent unnecessary re-renders
  const memoizedSrc = useMemo(() => src, [src]);
  const memoizedFallbackSrc = useMemo(() => fallbackSrc, [fallbackSrc]);

  const { isLoading, isLoaded, hasError } = useImageLoader(memoizedSrc, {
    onLoad: onImageLoad,
    onError: onImageError,
    retryCount,
    retryDelay,
  });

  const { isLoaded: isFallbackLoaded, hasError: hasFallbackError } = useImageLoader(
    hasError && memoizedFallbackSrc ? memoizedFallbackSrc : undefined,
    {
      onLoad: onImageLoad,
      onError: onImageError,
      retryCount: 1, // Only retry fallback once
      retryDelay,
    }
  );

  const displaySrc = hasError && memoizedFallbackSrc ? memoizedFallbackSrc : memoizedSrc;
  const isDisplayLoaded = isLoaded || (hasError && isFallbackLoaded);
  const showFallbackError = hasError && !memoizedFallbackSrc;

  return (
    <div
      className={cn(
        'relative w-full h-full overflow-hidden',
        !noRadius && (rounded ? 'rounded-profile-pic' : 'rounded-md'),
        className
      )}
      style={props.style}
    >
      {/* Skeleton loader */}
      {showSkeleton && (isLoading || (!isDisplayLoaded && !showFallbackError)) && (
        <div
          className={cn(
            'absolute inset-0 w-full h-full bg-muted animate-pulse',
            !noRadius && (rounded ? 'rounded-profile-pic' : 'rounded-md')
          )}
        />
      )}
      
      {/* Error placeholder */}
      {showFallbackError && (
        <div
          className={cn(
            'absolute inset-0 w-full h-full bg-black flex items-center justify-center',
            !noRadius && (rounded ? 'rounded-profile-pic' : 'rounded-md')
          )}
        >
          <img
            src="/assets/img/placeholder.png"
            alt="GetLost Logo"
            className="object-contain max-w-[50%] max-h-[50%] opacity-50"
          />
        </div>
      )}
      
      {/* Image */}
      {displaySrc && (
        <img
          src={displaySrc}
          alt={alt}
          loading={loading}
          className={cn(
            'absolute inset-0 w-full h-full object-cover transition-opacity',
            !noRadius && (rounded ? 'rounded-profile-pic' : 'rounded-md'),
            isDisplayLoaded ? 'opacity-100 image-fade-in' : 'opacity-0',
          )}
          style={{
            transition: `opacity ${fadeInDuration}ms`,
          }}
          onLoad={(e) => {
            // Additional onLoad handler for the img element
            if (isDisplayLoaded) {
              onImageLoad?.();
            }
          }}
          onError={(e) => {
            // Additional onError handler for the img element
            if (!isDisplayLoaded) {
              onImageError?.(`Failed to load image: ${displaySrc}`);
            }
          }}
          {...props}
        />
      )}
    </div>
  );
};

interface BackgroundImageProps {
  src: string;
  fallbackSrc?: string;
  showSkeleton?: boolean;
  fadeInDuration?: number;
  onImageLoad?: () => void;
  onImageError?: (error: string) => void;
  className?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  rounded?: boolean;
  noRadius?: boolean;
  loading?: 'eager' | 'lazy'; // prioritize loading for first items
  retryCount?: number;
  retryDelay?: number;
}

export const BackgroundImage: React.FC<BackgroundImageProps> = ({
  src,
  fallbackSrc,
  showSkeleton = true,
  fadeInDuration = 300,
  onImageLoad,
  onImageError,
  className,
  children,
  style,
  rounded = false,
  noRadius = false,
  loading = 'lazy', // default to lazy loading
  retryCount = 2,
  retryDelay = 1000,
  ...props
}) => {
  // Memoize the src to prevent unnecessary re-renders
  const memoizedSrc = useMemo(() => src, [src]);
  const memoizedFallbackSrc = useMemo(() => fallbackSrc, [fallbackSrc]);

  const { isLoading, isLoaded, hasError } = useImageLoader(memoizedSrc, {
    onLoad: onImageLoad,
    onError: onImageError,
    retryCount,
    retryDelay,
  });

  const { isLoaded: isFallbackLoaded, hasError: hasFallbackError } = useImageLoader(
    hasError && memoizedFallbackSrc ? memoizedFallbackSrc : undefined,
    {
      onLoad: onImageLoad,
      onError: onImageError,
      retryCount: 1, // Only retry fallback once
      retryDelay,
    }
  );

  const displaySrc = hasError && memoizedFallbackSrc ? memoizedFallbackSrc : memoizedSrc;
  const isDisplayLoaded = isLoaded || (hasError && isFallbackLoaded);
  const showFallbackError = hasError && !memoizedFallbackSrc;

  return (
    <div
      className={cn(
        'relative w-full h-full overflow-hidden',
        !noRadius && (rounded ? 'rounded-profile-pic' : 'rounded-md'),
        className
      )}
      style={{
        ...style,
      }}
      {...props}
    >
      {/* Skeleton loader */}
      {showSkeleton && (isLoading || (!isDisplayLoaded && !showFallbackError)) && (
        <div
          className={cn(
            'absolute inset-0 w-full h-full bg-muted animate-pulse',
            !noRadius && (rounded ? 'rounded-profile-pic' : 'rounded-md')
          )}
        />
      )}
      
      {/* Error placeholder */}
      {showFallbackError && (
        <div
          className={cn(
            'absolute inset-0 w-full h-full bg-black flex items-center justify-center',
            !noRadius && (rounded ? 'rounded-profile-pic' : 'rounded-md')
          )}
        >
          <img
            src="/assets/img/placeholder.png"
            alt="GetLost Logo"
            className="max-w-[40%] max-h-[40%] object-contain opacity-50"
          />
        </div>
      )}
      
      {/* Background image layer */}
      {displaySrc && (
        <div
          className={cn(
            'absolute inset-0 w-full h-full transition-opacity bg-center bg-cover bg-no-repeat',
            !noRadius && (rounded ? 'rounded-profile-pic' : 'rounded-md'),
            isDisplayLoaded ? 'opacity-100 image-fade-in' : 'opacity-0'
          )}
          style={{
            backgroundImage: isDisplayLoaded ? `url(${displaySrc})` : undefined,
            transition: `opacity ${fadeInDuration}ms`,
          }}
        />
      )}
      
      {/* Content */}
      <div className="relative z-10 w-full h-full">{children}</div>
    </div>
  );
}; 